import * as React from 'react';
import { CodeTwoTone, ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { EXPAND_LABEL, SHRINK_LABEL } from '@constants/common';
import { PROMPT_EDITOR_TITLE } from '@constants/prompt-explorer';
import { IPromptNode } from '@redux/types/types';
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator';
import { IPipelinePromptPlaygroundProps } from '../PipelinePlayground';
import styles from '../pipelinePlayground.module.scss';

interface IRenderPromptEditorProps {
  displayHeader: boolean;
  displayExpandShrinkButton: boolean;
  promptEditorContainerExpanded: boolean;
  onExpandShrinkPromptEditorContainer: () => void;
  pipelineParamsTemplate: string;
  selectedPromptNode: string;
  getUpdatePipelinePromptTooltipText: () => string | JSX.Element;
  isUpdatePipelineButtonDisabled: () => boolean;
  onUpdatePipelinePromptClick: () => void;
  onPromptEditorPromptNodeChange: IPipelinePromptPlaygroundProps['onPromptEditorPromptNodeChange'];
  onPromptEditorValueChange: IPipelinePromptPlaygroundProps['onPromptEditorValueChange'];
  onTemplatesLinkClick: IPipelinePromptPlaygroundProps['onTemplatesLinkClick'];
  prompts: Record<string, IPromptNode>;
  playgroundId: string;
}

const PromptEditor = React.lazy(
  () => import(/* webpackChunkName: "PromptEditor" */ '@components/promptEditor/PromptEditor'),
);

export const RenderPromptEditor: React.FC<IRenderPromptEditorProps> = ({
  displayExpandShrinkButton,
  displayHeader,
  promptEditorContainerExpanded,
  onExpandShrinkPromptEditorContainer,
  selectedPromptNode,
  pipelineParamsTemplate,
  getUpdatePipelinePromptTooltipText,
  isUpdatePipelineButtonDisabled,
  onUpdatePipelinePromptClick,
  onPromptEditorPromptNodeChange,
  onPromptEditorValueChange,
  playgroundId,
  onTemplatesLinkClick,
  prompts,
}) => (
  <div className={styles.promptEditor}>
    <div className={styles.promptEditor_header}>
      {displayHeader && (
        <div className={styles.promptEditor_header_wrapper}>
          <div className={styles.promptEditor_header_icon}>
            <CodeTwoTone />
          </div>
          <div className={styles.promptEditor_header_title}>{PROMPT_EDITOR_TITLE}</div>
        </div>
      )}
      {!displayHeader && <div className={styles.promptEditor_header_empty} />}
      {displayExpandShrinkButton && (
        <Tooltip
          placement="topLeft"
          title={promptEditorContainerExpanded ? SHRINK_LABEL : EXPAND_LABEL}
        >
          <Button
            size="small"
            icon={promptEditorContainerExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            type="text"
            onClick={onExpandShrinkPromptEditorContainer}
          />
        </Tooltip>
      )}
    </div>
    <div className={styles.promptEditor_editor}>
      <React.Suspense fallback={<LoadingIndicator />}>
        <PromptEditor
          prompt={pipelineParamsTemplate}
          promptNodes={Object.keys(prompts)}
          selectedPromptNode={selectedPromptNode}
          updatePipelinePromptButtonProps={{
            tooltipTitle: getUpdatePipelinePromptTooltipText(),
            disabled: isUpdatePipelineButtonDisabled(),
            onClick: onUpdatePipelinePromptClick,
          }}
          onPromptNodeSelect={(newNode) => {
            onPromptEditorPromptNodeChange({ node: newNode, playgroundId });
          }}
          onChange={(newValue) => {
            onPromptEditorValueChange({ value: newValue, playgroundId });
          }}
          onTemplatesLinkClick={() => onTemplatesLinkClick(playgroundId)}
        />
      </React.Suspense>
    </div>
  </div>
);
